/**
 * Bill Memory page API (backend routes/billMemory.js). The server scopes everything to the
 * signed-in business and enforces who may read and change it.
 */
import http from '../services/httpClient';

const B = '/bill-memory';
const qs = (q) => {
  const p = new URLSearchParams();
  Object.entries(q || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, v); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const getAccess = () => http.get(`${B}/access`);
export const getLessons = (filters) => http.get(`${B}/lessons${qs(filters)}`);
export const addLesson = (body) => http.post(`${B}/lessons`, body);
export const patchLesson = (id, body) => http.patch(`${B}/lessons/${id}`, body);
export const forget = (id) => http.delete(`${B}/lessons/${id}`);
export const undo = (token) => http.post(`${B}/undo/${encodeURIComponent(token)}`, {});
export const forgetByUser = (userId, preview) => http.post(`${B}/forget-by-user`, { user_id: userId, preview: !!preview });
export const getVendors = () => http.get(`${B}/vendors`);
export const getDictionary = () => http.get(`${B}/dictionary`);
export const setGroup = (groupKey, on) => http.patch(`${B}/dictionary/${encodeURIComponent(groupKey)}`, { on });
export const getActivity = () => http.get(`${B}/activity`);
export const setOwnGstin = (gstin) => http.put(`${B}/own-gstin`, { gstin });
// Fix 12: the vendor-progress line and the one-time introduction
export const getVendorProgress = (vendorId) => http.get(`${B}/vendor-progress${qs({ vendor_id: vendorId })}`);
export const dismissIntro = () => http.post(`${B}/tips/intro/dismiss`, {});
// Fix plan I3 / I2: the business's own check, recent greens and "This was wrong"
export const getCheck = () => http.get(`${B}/check`);
export const markCheck = (id, verdict) => http.post(`${B}/check/${id}`, { verdict });
export const getRecentGreens = () => http.get(`${B}/recent-greens`);
export const markLineWrong = (lineId, itemId) => http.post(`${B}/lines/${lineId}/wrong`, itemId ? { item_id: itemId } : {});
// Fix 13 row 1: forget one learned vendor clue (a GSTIN or phone)
export const forgetClue = (id) => http.delete(`${B}/vendor-clues/${id}`);

/** The business's items, for the item pickers. */
export async function getInventory(businessId) {
  const r = await http.get(`/inventory/items/${businessId}`);
  const d = (r && r.data && r.data.data) || (r && r.data) || r;
  return Array.isArray(d) ? d : [];
}

const apiBase = () => {
  const env = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
  if (!env) return '/api';
  const clean = env.replace(/\/+$/, '');
  return clean.endsWith('/api') ? clean : `${clean}/api`;
};

/** Export: the table as a spreadsheet, downloaded through a blob (the API URL is never opened). */
export async function downloadExport() {
  const headers = {};
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${apiBase()}${B}/export`, { headers });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bill-memory.xlsx';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Invexis admin (design §16)
export const getAdminBusiness = (businessId) => http.get(`${B}/admin/business/${businessId}`);
export const putSwitches = (businessId, body) => http.put(`${B}/admin/business/${businessId}/switches`, body);
export const putGlobal = (body) => http.put(`${B}/admin/global`, body);
export const putDictStatus = (version, status) => http.put(`${B}/admin/dictionary/${version}/status`, { status });
export const backToDefault = (businessId) => http.post(`${B}/admin/business/${businessId}/default`, {});
export const runImport = (businessId) => http.post(`${B}/admin/business/${businessId}/import`, {});
// Fix 3B + I3: the Invexis side of the weekly audit
export const getAudit = () => http.get(`${B}/admin/audit`);
export const markAudit = (id, verdict, forget) => http.post(`${B}/admin/audit/${id}`, { verdict, forget: !!forget });
// Fix plan, Monitoring + Fix 4: the Health view and the Ready list (every business)
export const getHealth = () => http.get(`${B}/admin/health`);
export const getReady = () => http.get(`${B}/admin/ready`);
export const answerReady = (businessId, source, verdict, note) => http.post(`${B}/admin/ready/${businessId}/${source}/${verdict}`, note ? { note } : {});
